import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import useSettings from "../../hooks/useSettings";
import { parseSipSettings, SIP_SETTING_KEYS } from "../../utils/sipAsterisk";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
  },
  paper: {
    padding: theme.spacing(3),
  },
  actions: {
    marginTop: theme.spacing(2),
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
  },
  helperBox: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    borderRadius: 8,
    backgroundColor: theme.mode === "light" ? "#f7f9fc" : "rgba(255,255,255,0.06)",
    border: `1px solid ${theme.mode === "light" ? "#d9e2ef" : "rgba(255,255,255,0.12)"}`,
  },
  formControl: {
    width: "100%",
  },
}));

const INITIAL_FORM = {
  sipEnabled: "disabled",
  sipWebsocketUrl: "",
  sipDomain: "",
  sipUsername: "",
  sipPassword: "",
  sipDisplayName: "",
  sipDialPrefix: "",
  sipTargetDomain: "",
};

const SipAsteriskSettings = ({ settings }) => {
  const classes = useStyles();
  const { update } = useSettings();
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);

  const parsedSettings = useMemo(() => parseSipSettings(settings), [settings]);

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      ...parsedSettings.raw,
      sipEnabled: parsedSettings.enabled ? "enabled" : "disabled",
      sipWebsocketUrl: parsedSettings.websocketUrl,
      sipDomain: parsedSettings.domain,
      sipUsername: parsedSettings.username,
      sipPassword: parsedSettings.password,
      sipDisplayName: parsedSettings.displayName,
      sipDialPrefix: parsedSettings.dialPrefix,
      sipTargetDomain: parsedSettings.targetDomain === parsedSettings.domain ? "" : parsedSettings.targetDomain,
    }));
  }, [parsedSettings]);

  const handleChange = event => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async event => {
    event.preventDefault();
    setLoading(true);

    try {
      for (const key of SIP_SETTING_KEYS) {
        await update({
          key,
          value: formData[key] ?? "",
        });
      }

      toast.success("Configuração SIP salva com sucesso.");
    } catch (error) {
      toast.error(error?.response?.data?.error || error?.message || "Não foi possível salvar a configuração SIP.");
    }

    setLoading(false);
  };

  return (
    <div className={classes.root}>
      <Paper className={classes.paper} elevation={0}>
        <form onSubmit={handleSave}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">Integração SIP / Asterisk</Typography>
              <Typography variant="body2" color="textSecondary">
                Configure o ramal SIP que será usado quando o atendente clicar no botão de ligação dentro do ticket.
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel id="sipEnabled-label">Telefonia SIP</InputLabel>
                <Select
                  labelId="sipEnabled-label"
                  label="Telefonia SIP"
                  name="sipEnabled"
                  value={formData.sipEnabled}
                  onChange={handleChange}
                >
                  <MenuItem value="enabled">Habilitada</MenuItem>
                  <MenuItem value="disabled">Desabilitada</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                variant="outlined"
                fullWidth
                name="sipWebsocketUrl"
                label="URL WebSocket SIP"
                placeholder="wss://pbx.seudominio.com:8089/ws"
                value={formData.sipWebsocketUrl}
                onChange={handleChange}
                helperText="Obrigatório para o navegador registrar no Asterisk via WSS/WS."
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                variant="outlined"
                fullWidth
                name="sipDomain"
                label="Domínio SIP"
                placeholder="pbx.seudominio.com"
                value={formData.sipDomain}
                onChange={handleChange}
                helperText="Host usado no URI do ramal."
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                variant="outlined"
                fullWidth
                name="sipUsername"
                label="Usuário / Ramal SIP"
                placeholder="202"
                value={formData.sipUsername}
                onChange={handleChange}
                helperText="Ex.: número do ramal autenticado no Asterisk."
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                variant="outlined"
                fullWidth
                name="sipPassword"
                label="Senha SIP"
                type="password"
                value={formData.sipPassword}
                onChange={handleChange}
                helperText="Senha do ramal SIP."
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                variant="outlined"
                fullWidth
                name="sipDisplayName"
                label="Nome de exibição"
                placeholder="Atendimento"
                value={formData.sipDisplayName}
                onChange={handleChange}
                helperText="Nome apresentado durante a ligação."
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                variant="outlined"
                fullWidth
                name="sipDialPrefix"
                label="Prefixo de discagem"
                placeholder="0 ou 55"
                value={formData.sipDialPrefix}
                onChange={handleChange}
                helperText="Opcional. Será adicionado antes do telefone do contato."
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                variant="outlined"
                fullWidth
                name="sipTargetDomain"
                label="Domínio de destino"
                placeholder="tronco.seudominio.com"
                value={formData.sipTargetDomain}
                onChange={handleChange}
                helperText="Opcional. Se vazio, será usado o Domínio SIP."
              />
            </Grid>
          </Grid>

          <div className={classes.actions}>
            <Button type="submit" color="primary" variant="contained" disabled={loading}>
              {loading ? "Salvando..." : "Salvar configuração"}
            </Button>
          </div>
        </form>

        <div className={classes.helperBox}>
          <Typography variant="subtitle2">Como essa integração funciona</Typography>
          <Typography variant="body2" color="textSecondary">
            Depois de salvar e habilitar a telefonia SIP, o botão de telefone do ticket passa a iniciar uma ligação WebRTC
            para o número do contato usando o seu servidor Asterisk.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Garanta que o Asterisk esteja com transporte WebSocket habilitado e que o navegador tenha permissão para usar o microfone.
          </Typography>
        </div>
      </Paper>
    </div>
  );
};

export default SipAsteriskSettings;
